import React, { useEffect, useRef, useState } from "react";
import { collection, documentId, getCountFromServer, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useAuth } from "../../AuthContext";

// Admin-only: put every US bar into the app from Foursquare's free open list
// (the us_bars.json file made by tools/extract_us_bars.py), then read bars'
// own websites for specials and events one state at a time.

const NATIONAL_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/nationalBars";
const LOADER_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/importAreaBars";
const IMPORT_BATCH = 400;
const WEB_BATCH = 5; // bars per request
const WEB_WORKERS = 3; // requests at the same time
const COST_PER_SITE = 0.05; // rough Claude cost per website read
const PROGRESS_KEY = "bff_national_import_progress";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI",
  "SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

async function post(url, body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// Retries a few times, because one hiccup shouldn't stop a 20-minute job.
async function postWithRetry(url, body, tries = 3) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await post(url, body);
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 2000 * (i + 1)));
    }
  }
  throw lastErr;
}

function readProgress() {
  try {
    return JSON.parse(localStorage.getItem(PROGRESS_KEY) || "null");
  } catch {
    return null;
  }
}
function saveProgress(p) {
  try {
    if (p) localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
    else localStorage.removeItem(PROGRESS_KEY);
  } catch {
    /* private window: progress just isn't remembered */
  }
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "16px", color: "#fff" };
const btn = {
  minHeight: "44px", padding: "0 18px", borderRadius: "10px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer", marginRight: "10px",
};
const pink = { ...btn, background: "#FF3D81", color: "#0B0B12" };
const gray = { ...btn, background: "#2A2238", color: "#fff" };
const muted = { color: "#B8B0C8", fontSize: "15px", lineHeight: 1.5 };
const select = {
  minHeight: "44px", padding: "0 10px", borderRadius: "10px", border: "1px solid #3A3350",
  background: "#0B0B12", color: "#fff", font: "500 15px Inter, sans-serif", marginRight: "10px",
};

export default function AdminNationalBars() {
  const { role } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  // Step 1
  const [prep, setPrep] = useState({ running: false, scanned: 0, updated: 0, done: false });

  // Step 2
  const [fileBars, setFileBars] = useState(null); // parsed lines
  const [fileName, setFileName] = useState("");
  const [onlyState, setOnlyState] = useState("ALL");
  const [imp, setImp] = useState({ running: false, sent: 0, total: 0, created: 0, updated: 0, linked: 0, skipped: 0 });

  // Step 3
  const [webState, setWebState] = useState("MN");
  const [webLeft, setWebLeft] = useState(null);
  const [web, setWeb] = useState({ running: false, done: 0, statuses: {} });

  const [older, setOlder] = useState({ running: false, queued: 0, done: false });
  const [picTest, setPicTest] = useState({ running: false, rows: [] });
  const [requeue, setRequeue] = useState({ running: false, queued: 0, done: false });
  const [menus, setMenus] = useState({ running: false, checked: 0, found: 0, done: false });
  const [retag, setRetag] = useState({ running: false, scanned: 0, updated: 0, done: false });
  const [dupes, setDupes] = useState({ running: false, step: "", bars: 0, events: 0, specialsRemoved: 0, eventsRemoved: 0, done: false });

  const stopRef = useRef(false);

  const refreshStats = async () => {
    try {
      setStats(await post(NATIONAL_URL, { action: "stats" }));
    } catch (e) {
      setError(e.message);
    }
  };

  const refreshWebLeft = async (st) => {
    try {
      const q = query(collection(db, "bars"), where("state", "==", st), where("webChecked", "==", false));
      setWebLeft((await getCountFromServer(q)).data().count);
    } catch (e) {
      setWebLeft(null);
      setError(e.message);
    }
  };

  useEffect(() => {
    if (role === "admin") refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (role === "admin") refreshWebLeft(webState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, webState]);

  if (role !== "admin") return <p style={{ color: "#fff" }}>Admins only.</p>;

  // ---------------- Step 1: area codes for bars already in the app -------------
  const runPrep = async () => {
    setError("");
    stopRef.current = false;
    setPrep({ running: true, scanned: 0, updated: 0, done: false });
    let after;
    let scanned = 0;
    let updated = 0;
    try {
      for (;;) {
        if (stopRef.current) break;
        const r = await postWithRetry(NATIONAL_URL, { action: "backfill", after });
        scanned += r.scanned;
        updated += r.updated;
        setPrep({ running: true, scanned, updated, done: false });
        if (r.done || !r.next) {
          setPrep({ running: false, scanned, updated, done: true });
          break;
        }
        after = r.next;
      }
    } catch (e) {
      setError(`Prepare stopped: ${e.message}`);
    }
    setPrep((p) => ({ ...p, running: false }));
    refreshStats();
  };

  // ---------------- Step 2: import the file -----------------------------------
  const onFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const list = [];
    let bad = 0;
    for (const line of text.split("\n")) {
      if (!line.trim()) continue;
      try {
        list.push(JSON.parse(line));
      } catch {
        bad++;
      }
    }
    if (list.length === 0) {
      setError("That file has no bars in it. Pick us_bars.json from the tools folder.");
      return;
    }
    if (bad) setError(`${bad} lines couldn't be read and will be skipped.`);
    setFileBars(list);
  };

  const countsByState = (() => {
    if (!fileBars) return [];
    const m = {};
    fileBars.forEach((b) => (m[b.state] = (m[b.state] || 0) + 1));
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();

  const chosen = fileBars ? (onlyState === "ALL" ? fileBars : fileBars.filter((b) => b.state === onlyState)) : [];
  const progressId = `${fileName}|${fileBars?.length || 0}|${onlyState}`;
  const saved = readProgress();
  const resumeAt = saved && saved.id === progressId ? saved.next : 0;

  const runImport = async (startAt) => {
    setError("");
    stopRef.current = false;
    const t = { running: true, sent: startAt, total: chosen.length, created: 0, updated: 0, linked: 0, skipped: 0 };
    setImp({ ...t });
    try {
      for (let i = startAt; i < chosen.length; i += IMPORT_BATCH) {
        if (stopRef.current) break;
        const r = await postWithRetry(NATIONAL_URL, { action: "import", bars: chosen.slice(i, i + IMPORT_BATCH) });
        t.sent = Math.min(i + IMPORT_BATCH, chosen.length);
        t.created += r.created;
        t.updated += r.updated;
        t.linked += r.linked;
        t.skipped += r.skipped;
        setImp({ ...t });
        saveProgress(t.sent >= chosen.length ? null : { id: progressId, next: t.sent });
      }
    } catch (e) {
      setError(`Import paused: ${e.message}. Click "Resume" to keep going from where it stopped.`);
    }
    setImp((p) => ({ ...p, running: false }));
    refreshStats();
    refreshWebLeft(webState);
  };

  // ---------------- Step 3: read websites for one state ------------------------
  const runWeb = async () => {
    setError("");
    stopRef.current = false;
    const tally = { running: true, done: 0, statuses: {} };
    const tried = new Set(); // never send the same bar twice in one run
    setWeb({ ...tally, statuses: {} });
    try {
      for (;;) {
        if (stopRef.current) break;
        const snap = await getDocs(
          query(collection(db, "bars"), where("state", "==", webState), where("webChecked", "==", false), limit(200))
        );
        const ids = snap.docs.map((d) => d.id).filter((id) => !tried.has(id)).slice(0, 60);
        if (ids.length === 0) break;
        ids.forEach((id) => tried.add(id));
        const chunks = [];
        for (let i = 0; i < ids.length; i += WEB_BATCH) chunks.push(ids.slice(i, i + WEB_BATCH));
        // A few requests at once, each reading 5 websites.
        let next = 0;
        const worker = async () => {
          while (!stopRef.current && next < chunks.length) {
            const chunk = chunks[next++];
            try {
              const r = await postWithRetry(LOADER_URL, { action: "load", placeIds: chunk }, 2);
              for (const x of r.results || []) {
                if (x.status === "not read yet (stopped)") continue; // will be read next time
                tally.statuses[x.status] = (tally.statuses[x.status] || 0) + 1;
                tally.done++;
              }
              if (r.stop) {
                // Credits ran out (or Claude is unavailable): stop everything.
                stopRef.current = true;
                setError(`Stopped: ${r.stop}`);
              }
            } catch (e) {
              tally.statuses["error"] = (tally.statuses["error"] || 0) + chunk.length;
              tally.done += chunk.length;
            }
            setWeb({ ...tally, statuses: { ...tally.statuses } });
          }
        };
        await Promise.all(Array.from({ length: WEB_WORKERS }, worker));
        await refreshWebLeft(webState);
      }
    } catch (e) {
      setError(`Website reading stopped: ${e.message}`);
    }
    setWeb((p) => ({ ...p, running: false }));
    refreshWebLeft(webState);
    refreshStats(); // update the "Done" list
  };

  // Put bars added before the national list in line for website reading.
  const runQueueOlder = async () => {
    setError("");
    stopRef.current = false;
    let after;
    let queued = 0;
    setOlder({ running: true, queued: 0, done: false });
    try {
      for (;;) {
        if (stopRef.current) break;
        const r = await postWithRetry(NATIONAL_URL, { action: "queueOlder", after });
        queued += r.queued;
        setOlder({ running: true, queued, done: false });
        if (r.done || !r.next) break;
        after = r.next;
      }
      setOlder({ running: false, queued, done: true });
    } catch (e) {
      setError(`Couldn't line up the older bars: ${e.message}`);
      setOlder((o) => ({ ...o, running: false }));
    }
    refreshWebLeft(webState);
  };

  // Re-read 10 bars that were already read (or had only pictures) now that
  // the reader also looks at pictures, to see how much more it finds.
  const runPictureTest = async () => {
    setError("");
    setPicTest({ running: true, rows: [] });
    try {
      const snap = await getDocs(
        query(
          collection(db, "bars"),
          where("state", "==", webState),
          where("webStatus", "in", ["website has no readable text", "ok"]),
          limit(60)
        )
      );
      const picks = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((b) => b.website && !b.ownerId && !b.charityId && !b.chain)
        .sort(() => Math.random() - 0.5)
        .slice(0, 10);
      if (picks.length === 0) {
        setError(`No read ${webState} bars to test yet. Read the state's websites first.`);
        setPicTest({ running: false, rows: [] });
        return;
      }
      const rows = [];
      for (let i = 0; i < picks.length; i += 5) {
        const chunk = picks.slice(i, i + 5);
        const r = await post(LOADER_URL, { action: "load", placeIds: chunk.map((b) => b.id) });
        (r.results || []).forEach((x, j) =>
          rows.push({
            name: chunk[j].name,
            before: chunk[j].webStatus === "ok" ? "Read before" : "No text before",
            status: x.status,
            specials: x.specials || 0,
            events: x.events || 0,
            pictures: x.pictures || 0,
          })
        );
        setPicTest({ running: true, rows: [...rows] });
        if (r.stop) {
          setError(`Stopped: ${r.stop}`);
          break;
        }
      }
      setPicTest({ running: false, rows });
    } catch (e) {
      setError(`Picture test stopped: ${e.message}`);
      setPicTest((t) => ({ ...t, running: false }));
    }
  };

  // Put a state's already-read bars back in line to be read again with pictures.
  const runRequeue = async (full = false) => {
    const what = full
      ? `read again in full (text + pictures, about 5–15¢ per bar). This also catches the new kinds of events.`
      : `read again for pictures only (cheaper; bars with no pictures cost nothing).`;
    if (!window.confirm(`Line up ${webState}'s already-read bars to be ${what} Then click Read ${webState} websites.`)) return;
    setError("");
    let after;
    let queued = 0;
    setRequeue({ running: true, queued: 0, done: false });
    try {
      for (;;) {
        const r = await postWithRetry(NATIONAL_URL, { action: "requeueState", state: webState, after, full });
        queued += r.queued;
        setRequeue({ running: true, queued, done: false });
        if (r.done || !r.next) break;
        after = r.next;
      }
      setRequeue({ running: false, queued, done: true });
    } catch (e) {
      setError(`Couldn't line them up: ${e.message}`);
      setRequeue((x) => ({ ...x, running: false }));
    }
    refreshWebLeft(webState);
  };

  // Free: look for each read bar's menu page/PDF so the app can show a Menu button.
  const runMenus = async () => {
    setError("");
    stopRef.current = false;
    const t = { running: true, checked: 0, found: 0, done: false };
    setMenus({ ...t });
    let last = null;
    try {
      for (;;) {
        if (stopRef.current) break;
        let q = query(
          collection(db, "bars"),
          where("state", "==", webState),
          where("webStatus", "in", ["ok", "website has no readable text"]),
          orderBy(documentId()),
          limit(60)
        );
        if (last) q = query(q, startAfter(last));
        const snap = await getDocs(q);
        if (snap.empty) break;
        last = snap.docs[snap.docs.length - 1];
        const ids = snap.docs.filter((d) => !d.data().menuUrl).map((d) => d.id);
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
        await Promise.all(
          chunks.map(async (chunk) => {
            const r = await postWithRetry(LOADER_URL, { action: "menus", placeIds: chunk }, 2).catch(() => ({ results: [] }));
            for (const x of r.results || []) {
              t.checked++;
              if (x.status === "menu found") t.found++;
            }
            setMenus({ ...t });
          })
        );
        if (snap.size < 60) break;
      }
      setMenus({ ...t, running: false, done: true });
    } catch (e) {
      setError(`Menu search stopped: ${e.message}`);
      setMenus({ ...t, running: false });
    }
  };

  // Free: file events already in the app under the newer pills by their title.
  const runRetag = async () => {
    setError("");
    let after;
    const t = { running: true, scanned: 0, updated: 0, done: false, bySub: {} };
    setRetag({ ...t });
    try {
      for (;;) {
        const r = await postWithRetry(NATIONAL_URL, { action: "retagEvents", after });
        t.scanned += r.scanned;
        t.updated += r.updated;
        Object.entries(r.bySub || {}).forEach(([k, v]) => (t.bySub[k] = (t.bySub[k] || 0) + v));
        setRetag({ ...t, bySub: { ...t.bySub } });
        if (r.done || !r.next) break;
        after = r.next;
      }
      setRetag({ ...t, running: false, done: true });
    } catch (e) {
      setError(`Sorting events stopped: ${e.message}`);
      setRetag({ ...t, running: false });
    }
  };

  // Remove the same special/event saved twice with different wording
  // (e.g. "Friday Fish Fry" + "All You Can Eat Fish Fry"). All states.
  const runDupes = async () => {
    setError("");
    const t = { running: true, step: "specials", bars: 0, events: 0, specialsRemoved: 0, eventsRemoved: 0, done: false };
    setDupes({ ...t });
    try {
      let after;
      for (;;) {
        const r = await postWithRetry(NATIONAL_URL, { action: "dedupeSpecials", after });
        t.bars += r.scanned;
        t.specialsRemoved += r.removed;
        setDupes({ ...t });
        if (r.done || !r.next) break;
        after = r.next;
      }
      t.step = "events";
      setDupes({ ...t });
      after = undefined;
      for (;;) {
        const r = await postWithRetry(NATIONAL_URL, { action: "dedupeEvents", after });
        t.events += r.scanned;
        t.eventsRemoved += r.removed;
        setDupes({ ...t });
        if (r.done || !r.next) break;
        after = r.next;
      }
      setDupes({ ...t, running: false, done: true });
    } catch (e) {
      setError(`Removing duplicates stopped: ${e.message}. Click it again to pick up; nothing gets removed twice.`);
      setDupes({ ...t, running: false });
    }
  };

  const busy =
    prep.running || imp.running || web.running || older.running || picTest.running || requeue.running || menus.running || retag.running || dupes.running;
  const stopButton = busy && (
    <button style={gray} onClick={() => (stopRef.current = true)}>
      Stop
    </button>
  );

  // Where each state stands, so states can be done in any order.
  const byState = stats?.byState || {};
  const stateStatus = (s) => {
    const p = byState[s];
    if (!p || !p.added) return "not added yet";
    if (p.waiting > 0) return `added · ${p.waiting.toLocaleString()} websites left to read`;
    return "✓ done";
  };
  const doneStates = STATES.filter((s) => byState[s]?.added && !byState[s]?.waiting);
  const halfStates = STATES.filter((s) => byState[s]?.added && byState[s]?.waiting > 0);

  const stateSelect = (
    <select style={select} value={webState} onChange={(e) => setWebState(e.target.value)} disabled={busy}>
      {STATES.map((s) => (
        <option key={s} value={s}>
          {s} — {stateStatus(s)}
        </option>
      ))}
    </select>
  );
  const extraRow = { margin: "0 0 14px", paddingBottom: "14px", borderBottom: "1px solid #241E33" };

  return (
    <div style={{ color: "#fff", maxWidth: "900px" }}>
      <h2 style={{ marginBottom: "6px" }}>National bars</h2>
      <p style={{ ...muted, marginBottom: "18px" }}>
        Adding a new state takes two steps: <strong>add it</strong>, then <strong>read its websites</strong>. Keep this tab
        open while a step runs. Running a step again never duplicates anything, and claimed bars are never changed.
      </p>

      {error && <div style={{ ...box, background: "#3A1420", color: "#FFC2D1" }}>{error}</div>}

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>Bars in the app</h3>
        {stats ? (
          <p style={muted}>
            <strong style={{ color: "#fff", fontSize: "20px" }}>{stats.total.toLocaleString()}</strong> total ·{" "}
            {stats.fromFoursquare.toLocaleString()} from the national list
          </p>
        ) : (
          <p style={muted}>Loading…</p>
        )}
        {stats && (
          <div style={{ marginTop: "10px" }}>
            <p style={{ ...muted, margin: "0 0 4px" }}>
              <strong style={{ color: "#4ADE80" }}>Done ({doneStates.length}):</strong>{" "}
              {doneStates.length ? doneStates.join(", ") : "none yet"}
            </p>
            {halfStates.length > 0 && (
              <p style={{ ...muted, margin: "0 0 4px" }}>
                <strong style={{ color: "#FBBF24" }}>Added, websites not read yet:</strong>{" "}
                {halfStates.map((s) => `${s} (${byState[s].waiting.toLocaleString()} left)`).join(", ")}
              </p>
            )}
            <p style={{ ...muted, margin: 0 }}>
              <strong style={{ color: "#fff" }}>Not added yet:</strong> {STATES.length - doneStates.length - halfStates.length} states
            </p>
          </div>
        )}
      </div>

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>Step 1 · Add a state</h3>
        <p style={{ ...muted, marginBottom: "12px" }}>
          Click <strong>Choose File</strong> and pick <strong>us_bars.json</strong> from your{" "}
          <strong>bar_buzz_dashboard → tools</strong> folder. Pick the state, then click <strong>Add</strong>. Takes a
          minute or two per state. Bars that aren't really bars (nature centers, parks…) are skipped.
        </p>
        <input type="file" accept=".json,.jsonl,application/json" onChange={onFile} disabled={busy} style={{ marginBottom: "12px" }} />
        {fileBars && (
          <>
            <div style={{ margin: "4px 0 12px" }}>
              <select style={select} value={onlyState} onChange={(e) => setOnlyState(e.target.value)} disabled={busy}>
                <option value="ALL">All states ({fileBars.length.toLocaleString()})</option>
                {countsByState
                  .slice()
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([s, n]) => (
                    <option key={s} value={s}>
                      {s} ({n.toLocaleString()} bars){byState[s]?.added ? " — ✓ already added" : ""}
                    </option>
                  ))}
              </select>
              {resumeAt > 0 && !imp.running ? (
                <>
                  <button style={pink} disabled={busy} onClick={() => runImport(resumeAt)}>
                    Resume at bar {resumeAt.toLocaleString()}
                  </button>
                  <button style={gray} disabled={busy} onClick={() => { saveProgress(null); runImport(0); }}>
                    Start over
                  </button>
                </>
              ) : (
                <button style={pink} disabled={busy} onClick={() => runImport(0)}>
                  {imp.running ? "Adding…" : `Add ${chosen.length.toLocaleString()} bars`}
                </button>
              )}
              {imp.running && stopButton}
            </div>
            {(imp.running || imp.sent > 0) && (
              <>
                <div style={{ height: "10px", background: "#2A2238", borderRadius: "6px", overflow: "hidden", margin: "6px 0 10px" }}>
                  <div style={{ height: "100%", width: `${imp.total ? (imp.sent / imp.total) * 100 : 0}%`, background: "#FF3D81" }} />
                </div>
                <p style={muted}>
                  {imp.sent.toLocaleString()} of {imp.total.toLocaleString()} · {imp.created.toLocaleString()} new ·{" "}
                  {imp.linked.toLocaleString()} matched to bars we had
                  {!imp.running && imp.sent >= imp.total && imp.total > 0 && " · ✓ Done. Now do Step 2."}
                </p>
              </>
            )}
          </>
        )}
      </div>

      <div style={box}>
        <h3 style={{ margin: "0 0 8px" }}>Step 2 · Read the state's websites</h3>
        <p style={{ ...muted, marginBottom: "12px" }}>
          Finds specials, events and menus on each bar's website, including pictures like menu boards and flyers.
          Costs roughly 5–15¢ per website on your Claude Console account; bars with no website, chains and claimed
          bars are free.
        </p>
        <div style={{ marginBottom: "10px" }}>
          {stateSelect}
          <button style={pink} disabled={busy || !webLeft} onClick={runWeb}>
            {web.running ? "Reading…" : `Read ${webState} websites`}
          </button>
          {web.running && stopButton}
        </div>
        <p style={muted}>
          {webLeft == null
            ? "Counting…"
            : webLeft === 0
            ? `No ${webState} bars waiting: either they've all been read ✓, or the state hasn't been added in Step 1 yet.`
            : `${webLeft.toLocaleString()} ${webState} bars waiting · at most about $${Math.ceil(webLeft * COST_PER_SITE).toLocaleString()} (usually much less)`}
        </p>
        {(web.running || web.done > 0) && (
          <div style={{ marginTop: "10px" }}>
            <p style={muted}>
              {web.done.toLocaleString()} bars checked this run{!web.running && " · stopped or finished"}
            </p>
            <ul style={{ ...muted, margin: "6px 0 0", paddingLeft: "20px" }}>
              {Object.entries(web.statuses)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                  <li key={k}>
                    {k}: {v.toLocaleString()}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      <details style={box}>
        <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: "17px" }}>Extras (you rarely need these)</summary>
        <p style={{ ...muted, margin: "10px 0 14px" }}>
          These use the state picked here: {stateSelect}
        </p>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Remove duplicates (free)</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>
            Finds the same special or event saved twice with different wording, like "Friday Fish Fry" and "All You Can
            Eat Fish Fry" on the same day and time, and keeps the one with more detail. Only removes copies read from
            websites; never anything a bar posted. All states. New website reads already skip duplicates.
          </p>
          <button style={gray} disabled={busy} onClick={runDupes}>
            {dupes.running ? "Removing duplicates…" : "Remove duplicates"}
          </button>
          {(dupes.running || dupes.done) && (
            <span style={muted}>
              {dupes.bars.toLocaleString()} bars checked · {dupes.specialsRemoved.toLocaleString()} duplicate specials removed
              {dupes.step === "events" || dupes.done
                ? ` · ${dupes.events.toLocaleString()} events checked · ${dupes.eventsRemoved.toLocaleString()} duplicate events removed`
                : ""}
              {dupes.done && " · ✓ Done"}
            </span>
          )}
        </div>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Sort events into the newer pills (free)</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>
            Files events already in the app (all states) under Card Tournament, Fish Fry, League Night, Dance Night,
            Food Night, Tasting and Fundraiser by their title. Only adds pills; never deletes anything.
          </p>
          <button style={gray} disabled={busy} onClick={runRetag}>
            {retag.running ? "Sorting…" : "Sort events"}
          </button>
          {(retag.running || retag.done) && (
            <span style={muted}>
              {retag.scanned.toLocaleString()} events checked · {retag.updated.toLocaleString()} sorted{retag.done && " · ✓ Done"}
            </span>
          )}
          {retag.bySub && Object.keys(retag.bySub).length > 0 && (
            <p style={{ ...muted, margin: "6px 0 0" }}>
              {Object.entries(retag.bySub).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          )}
        </div>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Find menu links for {webState} (free)</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>Already part of every new read. Only needed for bars read before menus were added.</p>
          <button style={gray} disabled={busy} onClick={runMenus}>
            {menus.running ? "Finding menus…" : `Find menu links for ${webState}`}
          </button>
          {menus.running && (
            <button style={gray} onClick={() => (stopRef.current = true)}>
              Stop
            </button>
          )}
          {(menus.running || menus.done) && (
            <span style={muted}>
              {menus.checked.toLocaleString()} checked · {menus.found.toLocaleString()} menus found{menus.done && " · ✓ Done"}
            </span>
          )}
        </div>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Test the reader on 10 {webState} bars (about $1)</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>Re-reads 10 already-read bars and shows what it would add now.</p>
          <button style={gray} disabled={busy} onClick={runPictureTest}>
            {picTest.running ? "Testing…" : `Test 10 ${webState} bars`}
          </button>
          {picTest.rows.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", fontSize: "14px" }}>
              <thead>
                <tr style={{ color: "#B8B0C8", textAlign: "left" }}>
                  <th style={{ padding: "6px" }}>Bar</th>
                  <th style={{ padding: "6px" }}>Result</th>
                  <th style={{ padding: "6px", textAlign: "right" }}>Pictures read</th>
                  <th style={{ padding: "6px", textAlign: "right" }}>New specials</th>
                  <th style={{ padding: "6px", textAlign: "right" }}>New events</th>
                </tr>
              </thead>
              <tbody>
                {picTest.rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #241E33" }}>
                    <td style={{ padding: "6px" }}>{r.name}</td>
                    <td style={{ padding: "6px" }}>{r.status}</td>
                    <td style={{ padding: "6px", textAlign: "right" }}>{r.pictures}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: r.specials ? "#4ADE80" : "#fff" }}>{r.specials}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: r.events ? "#4ADE80" : "#fff" }}>{r.events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!picTest.running && picTest.rows.length > 0 && (
            <p style={{ ...muted, margin: "8px 0 0" }}>
              Total new: {picTest.rows.reduce((a, r) => a + r.specials, 0)} specials, {picTest.rows.reduce((a, r) => a + r.events, 0)} events
              from {picTest.rows.reduce((a, r) => a + r.pictures, 0)} pictures.
            </p>
          )}
        </div>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Re-read {webState} websites</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>
            Lines the state's already-read bars up again; then click Read {webState} websites in Step 2. Only worth it
            if the test above finds a lot more.
          </p>
          <button style={gray} disabled={busy} onClick={() => runRequeue(false)}>
            {requeue.running ? "Lining up…" : "Pictures only (cheaper)"}
          </button>
          <button style={gray} disabled={busy} onClick={() => runRequeue(true)}>
            Full (text + pictures)
          </button>
          {(requeue.running || requeue.done) && (
            <span style={muted}>
              {requeue.queued.toLocaleString()} lined up{requeue.done && ` · ✓ Now click Read ${webState} websites`}
            </span>
          )}
        </div>

        <div style={extraRow}>
          <div style={{ fontWeight: 700 }}>Line up older bars</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>
            For bars added before the national list (Load Bars tab, by hand, gaming list). Then read their state in Step 2.
          </p>
          <button style={gray} disabled={busy} onClick={runQueueOlder}>
            {older.running ? "Lining up…" : "Line up older bars"}
          </button>
          {(older.running || older.done) && (
            <span style={muted}>
              {older.queued.toLocaleString()} lined up{older.done && " · ✓ Done"}
            </span>
          )}
        </div>

        <div>
          <div style={{ fontWeight: 700 }}>Prepare bars (one-time setup, already done)</div>
          <p style={{ ...muted, margin: "4px 0 8px" }}>Gives bars added by hand an area code. Only rerun if I ask you to.</p>
          <button style={gray} disabled={busy} onClick={runPrep}>
            {prep.running ? "Preparing…" : "Prepare bars"}
          </button>
          {(prep.running || prep.done) && (
            <span style={muted}>
              {prep.scanned.toLocaleString()} checked · {prep.updated.toLocaleString()} updated{prep.done && " · ✓ Done"}
            </span>
          )}
        </div>
      </details>
    </div>
  );
}
